import type { AxiosResponse } from 'axios';
import { AdvancedAnalysisResponse, ChatRequestParameters, ChatResponse } from './experimentalApiTypes';
import {
  Api as GeneratedApi,
  HttpClient,
  CreatePartFinderReportRequest,
  CreatePartFinderReportResponse,
  CreateTaxonomyNodeRequest,
  CreateTaxonomyNodeResponse,
  CreateTaxonomyRequest,
  CreateTaxonomyResponse,
  UpdateTaxonomyRequest,
  UpdateTaxonomyResponse,
  ListTaxonomiesResponse,
  GetTaxonomyResponse,
  ListTaxonomyNodesResponse,
  RenameTaxonomyNodeRequest,
  UpdateTaxonomyNodeResponse,
  ListPartFinderReportsResponse,
  GetPartFinderReportResponse,
  GetFolderFiltersResponse,
  GetReportGroupFiltersResponse,
  HasSubfolderDescriptionsResponse,
  FolderColumnVisibilityResponse,
  GetTaxonomyNodeResponse,
  TaxonomyNode,
  AssignLabelResponse,
  AssignLabelRequest,
} from './generatedApi';
import { FeatureFlagName } from '@/utils/featureFlags';

interface GetNodeAncestorsResponse {
  ancestors: TaxonomyNode[];
}

export interface SearchTaxonomyNodesResponse {
  results: { node: TaxonomyNode; ancestors: TaxonomyNode[] }[];
  pageData: {
    total: number;
    perPage: number;
    currentPage: number;
    lastPage: number;
    startIndex: number;
    endIndex: number;
  };
}

export interface ViewerFileResponse {
  data: ArrayBuffer;
}

export interface GetMetadataFieldsWithDataResponse {
  metadataFieldNames: string[];
}

export interface LabelOption {
  name: string;
  depth: number;
  count: number;
}

export interface FolderLabelOptionsResponse {
  labels: LabelOption[];
}

type GeneratedAssetsType = GeneratedApi<unknown>['assets'];

interface EnhancedAssetsType extends GeneratedAssetsType {
  getAdvancedAnalysis(id: string, tenantId: string): Promise<AxiosResponse<AdvancedAnalysisResponse>>;
  chat(
    id: string,
    tenantId: string,
    parameters: ChatRequestParameters,
    onChunk?: (chunk: string) => void
  ): Promise<AxiosResponse<ChatResponse>>;
  getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>>;
  deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>>;
  putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<void>>;
  putManifestCriticalGeometry(tenantId: string, assetId: string): Promise<AxiosResponse<{ analysisAssetId: string }>>;
}

export type FeatureFlags = {
  [key in FeatureFlagName]: boolean;
};

interface EnhancedTenantsType {
  getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>>;
  createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>>;
  listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>>;
  getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>>;
  deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>>;
  getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getRootMetadataFieldsWithData(tenantId: string): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getRootHasSubfolderDescriptions(tenantId: string): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>>;
  getFolderHasSubfolderDescriptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>>;
  getRootLabelOptions(tenantId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>>;
  getFolderLabelOptions(tenantId: string, folderId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>>;
  getRootColumnVisibility(tenantId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>>;
  getFolderColumnVisibility(tenantId: string, folderId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>>;
  getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>>;
}
export interface ImportTaxonomyNodeInput {
  name: string;
  children: ImportTaxonomyNodeInput[];
}

export interface ImportTaxonomyNodesRequest {
  nodes: ImportTaxonomyNodeInput[];
}

export interface ImportTaxonomyNodesResponse {
  importedCount: number;
}

export interface DiffTaxonomyImportRequest {
  nodes: ImportTaxonomyNodeInput[];
}

export interface DiffTaxonomyImportResponse {
  additions: { name: string; parentName: string | undefined; hasChildren: boolean }[];
  removals: {
    nodeId: string;
    name: string;
    parentName: string | undefined;
    hasChildren: boolean;
    labelCount: number;
  }[];
  moves: { nodeId: string; name: string; currentParentName: string | undefined; newParentName: string | undefined }[];
  suggestedRenames: {
    removalNodeId: string;
    removalName: string;
    additionName: string;
    parentName: string | undefined;
    labelCount: number;
    similarity: number;
  }[];
  unchangedCount: number;
}

export interface ReimportTaxonomyNodesRequest {
  nodes: ImportTaxonomyNodeInput[];
  keepNodeIds: string[];
  renameMap: Record<string, string>;
}

export interface ReimportTaxonomyNodesResponse {
  addedCount: number;
  removedCount: number;
  movedCount: number;
  renamedCount: number;
  deletedLabelCount: number;
}

export interface AutoLabelLibraryRequest {
  skipAutoLabeled?: boolean;
}

export interface StartAutoLabelLibraryResponse {
  jobId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
}

export interface AutoLabelLibraryJobView {
  id: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress: number;
  queuedCount: number;
  skippedCount: number;
  completedCount: number;
}

export interface GetAutoLabelLibraryJobResponse {
  job: AutoLabelLibraryJobView;
}

export interface GetActiveAutoLabelLibraryJobResponse {
  job?: AutoLabelLibraryJobView;
}

interface TaxonomyApi {
  listTaxonomies(tenantId: string): Promise<AxiosResponse<ListTaxonomiesResponse>>;
  getTaxonomy(tenantId: string, taxonomyId: string): Promise<AxiosResponse<GetTaxonomyResponse>>;
  createTaxonomy(tenantId: string, data: CreateTaxonomyRequest): Promise<AxiosResponse<CreateTaxonomyResponse>>;
  updateTaxonomy(
    tenantId: string,
    taxonomyId: string,
    data: UpdateTaxonomyRequest
  ): Promise<AxiosResponse<UpdateTaxonomyResponse>>;
  deleteTaxonomy(tenantId: string, taxonomyId: string): Promise<AxiosResponse<void>>;
  startAutoLabelLibrary(
    tenantId: string,
    taxonomyId: string,
    data?: AutoLabelLibraryRequest
  ): Promise<AxiosResponse<StartAutoLabelLibraryResponse>>;
  getAutoLabelLibraryJob(
    tenantId: string,
    taxonomyId: string,
    jobId: string
  ): Promise<AxiosResponse<GetAutoLabelLibraryJobResponse>>;
  getActiveAutoLabelLibraryJob(
    tenantId: string,
    taxonomyId: string
  ): Promise<AxiosResponse<GetActiveAutoLabelLibraryJobResponse>>;
  listTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    query?: { page?: number; perPage?: number; parentNodeId?: string }
  ): Promise<AxiosResponse<ListTaxonomyNodesResponse>>;
  createTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    data: CreateTaxonomyNodeRequest
  ): Promise<AxiosResponse<CreateTaxonomyNodeResponse>>;
  renameTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    nodeId: string,
    data: RenameTaxonomyNodeRequest
  ): Promise<AxiosResponse<UpdateTaxonomyNodeResponse>>;
  deleteTaxonomyNode(tenantId: string, taxonomyId: string, nodeId: string): Promise<AxiosResponse<void>>;
  importTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    data: ImportTaxonomyNodesRequest
  ): Promise<AxiosResponse<ImportTaxonomyNodesResponse>>;
  diffTaxonomyImport(
    tenantId: string,
    taxonomyId: string,
    data: DiffTaxonomyImportRequest
  ): Promise<AxiosResponse<DiffTaxonomyImportResponse>>;
  reimportTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    data: ReimportTaxonomyNodesRequest
  ): Promise<AxiosResponse<ReimportTaxonomyNodesResponse>>;
  assignLabel(
    tenantId: string,
    assetId: string,
    taxonomyId: string,
    data: AssignLabelRequest
  ): Promise<AxiosResponse<AssignLabelResponse>>;
  unassignLabel(tenantId: string, assetId: string, taxonomyId: string): Promise<AxiosResponse<void>>;
  autoLabelAsset(tenantId: string, assetId: string): Promise<AxiosResponse<void>>;
  getTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    nodeId: string
  ): Promise<AxiosResponse<GetTaxonomyNodeResponse>>;
  getNodeAncestors(
    tenantId: string,
    taxonomyId: string,
    nodeId: string
  ): Promise<AxiosResponse<GetNodeAncestorsResponse>>;
  searchTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    query: { query: string; page?: number; perPage?: number }
  ): Promise<AxiosResponse<SearchTaxonomyNodesResponse>>;
}

export class Api extends GeneratedApi<unknown> {
  declare assets: EnhancedAssetsType;
  declare tenants: EnhancedTenantsType;
  taxonomy: TaxonomyApi;

  constructor(http: HttpClient) {
    super(http);
    Object.assign(this.assets, {
      getAdvancedAnalysis: this.getAdvancedAnalysis.bind(this),
      chat: this.chat.bind(this),
      getSupplementalAssets: this.getSupplementalAssets.bind(this),
      deleteSupplementalAssets: this.deleteSupplementalAssets.bind(this),
      putCriticalGeometry: this.putCriticalGeometry.bind(this),
      putManifestCriticalGeometry: this.putManifestCriticalGeometry.bind(this),
    });

    if (!this.tenants) {
      this.tenants = {} as EnhancedTenantsType;
    }
    Object.assign(this.tenants, {
      getFeatureFlags: this.getFeatureFlags.bind(this),
      createPartFinderReport: this.createPartFinderReport.bind(this),
      listPartFinderReports: this.listPartFinderReports.bind(this),
      getPartFinderReport: this.getPartFinderReport.bind(this),
      deletePartFinderReport: this.deletePartFinderReport.bind(this),
      getRootFilters: this.getRootFilters.bind(this),
      getFolderFilters: this.getFolderFilters.bind(this),
      getRootMetadataFieldsWithData: this.getRootMetadataFieldsWithData.bind(this),
      getFolderMetadataFieldsWithData: this.getFolderMetadataFieldsWithData.bind(this),
      getRootHasSubfolderDescriptions: this.getRootHasSubfolderDescriptions.bind(this),
      getFolderHasSubfolderDescriptions: this.getFolderHasSubfolderDescriptions.bind(this),
      getRootLabelOptions: this.getRootLabelOptions.bind(this),
      getFolderLabelOptions: this.getFolderLabelOptions.bind(this),
      getRootColumnVisibility: this.getRootColumnVisibility.bind(this),
      getFolderColumnVisibility: this.getFolderColumnVisibility.bind(this),
      getReportGroupFilters: this.getReportGroupFilters.bind(this),
    });

    this.taxonomy = {
      listTaxonomies: this.listTaxonomies.bind(this),
      getTaxonomy: this.getTaxonomy.bind(this),
      createTaxonomy: this.createTaxonomy.bind(this),
      updateTaxonomy: this.updateTaxonomy.bind(this),
      deleteTaxonomy: this.deleteTaxonomy.bind(this),
      startAutoLabelLibrary: this.startAutoLabelLibrary.bind(this),
      getAutoLabelLibraryJob: this.getAutoLabelLibraryJob.bind(this),
      getActiveAutoLabelLibraryJob: this.getActiveAutoLabelLibraryJob.bind(this),
      listTaxonomyNodes: this.listTaxonomyNodes.bind(this),
      createTaxonomyNode: this.createTaxonomyNode.bind(this),
      renameTaxonomyNode: this.renameTaxonomyNode.bind(this),
      deleteTaxonomyNode: this.deleteTaxonomyNode.bind(this),
      importTaxonomyNodes: this.importTaxonomyNodes.bind(this),
      diffTaxonomyImport: this.diffTaxonomyImport.bind(this),
      reimportTaxonomyNodes: this.reimportTaxonomyNodes.bind(this),
      assignLabel: this.assignLabel.bind(this),
      unassignLabel: this.unassignLabel.bind(this),
      autoLabelAsset: this.autoLabelAsset.bind(this),
      getTaxonomyNode: this.getTaxonomyNode.bind(this),
      getNodeAncestors: this.getNodeAncestors.bind(this),
      searchTaxonomyNodes: this.searchTaxonomyNodes.bind(this),
    };
  }

  private async getAdvancedAnalysis(id: string, tenantId: string): Promise<AxiosResponse<AdvancedAnalysisResponse>> {
    return this.http.request<AdvancedAnalysisResponse>({
      path: `/tenants/${tenantId}/assets/${id}/advanced-analysis`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async chat(
    id: string,
    tenantId: string,
    parameters: ChatRequestParameters,
    onChunk?: (chunk: string) => void
  ): Promise<AxiosResponse<ChatResponse>> {
    return this.http.request<ChatResponse>({
      path: `/tenants/${tenantId}/assets/${id}/advanced-analysis/chat`,
      method: 'POST',
      secure: true,
      format: 'json',
      body: parameters,
      onDownloadProgress: progressEvent => {
        if (onChunk && progressEvent.event.target) {
          const chunk = progressEvent.event.target.responseText;
          onChunk(chunk);
        }
      },
    });
  }

  private async getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>> {
    return this.http.request<FeatureFlags>({
      path: `/tenants/${tenantId}/feature-flags`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>> {
    return this.http.request<CreatePartFinderReportResponse>({
      path: `/tenant/${tenantId}/part-finder-reports`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>> {
    return this.http.request<ListPartFinderReportsResponse>({
      path: `/tenant/${tenantId}/part-finder-reports`,
      method: 'GET',
      query: {
        ...(page !== undefined && { page }),
        ...(perPage !== undefined && { perPage }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>> {
    return this.http.request<GetPartFinderReportResponse>({
      path: `/tenant/${tenantId}/part-finder-reports/${id}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenant/${tenantId}/part-finder-reports/${id}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>> {
    return this.http.request<{ supplementalAssets: unknown[]; pageData: unknown }>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'GET',
      secure: true,
      format: 'json',
      query: { page, perPage },
    });
  }

  private async deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'DELETE',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/critical-geometry-analysis`,
      method: 'PATCH',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putManifestCriticalGeometry(
    tenantId: string,
    assetId: string
  ): Promise<AxiosResponse<{ analysisAssetId: string }>> {
    return this.http.request<{ analysisAssetId: string }>({
      path: `/tenants/${tenantId}/assets/${assetId}/manifest-critical-geometry`,
      method: 'PUT',
      secure: true,
      format: 'json',
    });
  }

  private async getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/root/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootMetadataFieldsWithData(
    tenantId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/root/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootHasSubfolderDescriptions(
    tenantId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>> {
    return this.http.request<HasSubfolderDescriptionsResponse>({
      path: `/tenants/${tenantId}/folders/root/has-subfolder-descriptions`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderHasSubfolderDescriptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>> {
    return this.http.request<HasSubfolderDescriptionsResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/has-subfolder-descriptions`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootLabelOptions(tenantId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>> {
    return this.http.request<FolderLabelOptionsResponse>({
      path: `/tenants/${tenantId}/folders/root/label-options`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderLabelOptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<FolderLabelOptionsResponse>> {
    return this.http.request<FolderLabelOptionsResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/label-options`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootColumnVisibility(tenantId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>> {
    return this.http.request<FolderColumnVisibilityResponse>({
      path: `/tenants/${tenantId}/folders/root/column-visibility`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderColumnVisibility(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<FolderColumnVisibilityResponse>> {
    return this.http.request<FolderColumnVisibilityResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/column-visibility`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>> {
    return this.http.request<GetReportGroupFiltersResponse>({
      path: `/tenant/${tenantId}/reports/${reportId}/groups/${groupId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async listTaxonomies(tenantId: string): Promise<AxiosResponse<ListTaxonomiesResponse>> {
    return this.http.request<ListTaxonomiesResponse>({
      path: `/tenants/${tenantId}/taxonomies`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getTaxonomy(tenantId: string, taxonomyId: string): Promise<AxiosResponse<GetTaxonomyResponse>> {
    return this.http.request<GetTaxonomyResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async createTaxonomy(
    tenantId: string,
    data: CreateTaxonomyRequest
  ): Promise<AxiosResponse<CreateTaxonomyResponse>> {
    return this.http.request<CreateTaxonomyResponse>({
      path: `/tenants/${tenantId}/taxonomies`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async updateTaxonomy(
    tenantId: string,
    taxonomyId: string,
    data: UpdateTaxonomyRequest
  ): Promise<AxiosResponse<UpdateTaxonomyResponse>> {
    return this.http.request<UpdateTaxonomyResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}`,
      method: 'PATCH',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async deleteTaxonomy(tenantId: string, taxonomyId: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async startAutoLabelLibrary(
    tenantId: string,
    taxonomyId: string,
    data?: AutoLabelLibraryRequest
  ): Promise<AxiosResponse<StartAutoLabelLibraryResponse>> {
    return this.http.request<StartAutoLabelLibraryResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/auto-label-library`,
      method: 'POST',
      body: data ?? {},
      secure: true,
      format: 'json',
    });
  }

  private async getAutoLabelLibraryJob(
    tenantId: string,
    taxonomyId: string,
    jobId: string
  ): Promise<AxiosResponse<GetAutoLabelLibraryJobResponse>> {
    return this.http.request<GetAutoLabelLibraryJobResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/auto-label-library/jobs/${jobId}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getActiveAutoLabelLibraryJob(
    tenantId: string,
    taxonomyId: string
  ): Promise<AxiosResponse<GetActiveAutoLabelLibraryJobResponse>> {
    return this.http.request<GetActiveAutoLabelLibraryJobResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/auto-label-library`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async listTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    query?: { page?: number; perPage?: number; parentNodeId?: string }
  ): Promise<AxiosResponse<ListTaxonomyNodesResponse>> {
    return this.http.request<ListTaxonomyNodesResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes`,
      method: 'GET',
      query: {
        ...(query?.page !== undefined && { page: query.page }),
        ...(query?.perPage !== undefined && { perPage: query.perPage }),
        ...(query?.parentNodeId !== undefined && { parentNodeId: query.parentNodeId }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async createTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    data: CreateTaxonomyNodeRequest
  ): Promise<AxiosResponse<CreateTaxonomyNodeResponse>> {
    return this.http.request<CreateTaxonomyNodeResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async renameTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    nodeId: string,
    data: RenameTaxonomyNodeRequest
  ): Promise<AxiosResponse<UpdateTaxonomyNodeResponse>> {
    return this.http.request<UpdateTaxonomyNodeResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/${nodeId}/name`,
      method: 'PATCH',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async deleteTaxonomyNode(tenantId: string, taxonomyId: string, nodeId: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/${nodeId}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async importTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    data: ImportTaxonomyNodesRequest
  ): Promise<AxiosResponse<ImportTaxonomyNodesResponse>> {
    return this.http.request<ImportTaxonomyNodesResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/import`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async diffTaxonomyImport(
    tenantId: string,
    taxonomyId: string,
    data: DiffTaxonomyImportRequest
  ): Promise<AxiosResponse<DiffTaxonomyImportResponse>> {
    return this.http.request<DiffTaxonomyImportResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/import/diff`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async reimportTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    data: ReimportTaxonomyNodesRequest
  ): Promise<AxiosResponse<ReimportTaxonomyNodesResponse>> {
    return this.http.request<ReimportTaxonomyNodesResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/import`,
      method: 'PUT',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async assignLabel(
    tenantId: string,
    assetId: string,
    taxonomyId: string,
    data: AssignLabelRequest
  ): Promise<AxiosResponse<AssignLabelResponse>> {
    return this.http.request<AssignLabelResponse>({
      path: `/tenants/${tenantId}/assets/${assetId}/label/${taxonomyId}`,
      method: 'PUT',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async unassignLabel(tenantId: string, assetId: string, taxonomyId: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/label/${taxonomyId}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async autoLabelAsset(tenantId: string, assetId: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/label/auto`,
      method: 'POST',
      secure: true,
      format: 'json',
    });
  }

  private async getTaxonomyNode(
    tenantId: string,
    taxonomyId: string,
    nodeId: string
  ): Promise<AxiosResponse<GetTaxonomyNodeResponse>> {
    return this.http.request<GetTaxonomyNodeResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/${nodeId}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getNodeAncestors(
    tenantId: string,
    taxonomyId: string,
    nodeId: string
  ): Promise<AxiosResponse<GetNodeAncestorsResponse>> {
    return this.http.request<GetNodeAncestorsResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/${nodeId}/ancestors`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async searchTaxonomyNodes(
    tenantId: string,
    taxonomyId: string,
    query: { query: string; page?: number; perPage?: number }
  ): Promise<AxiosResponse<SearchTaxonomyNodesResponse>> {
    return this.http.request<SearchTaxonomyNodesResponse>({
      path: `/tenants/${tenantId}/taxonomies/${taxonomyId}/nodes/search`,
      method: 'GET',
      query: {
        query: query.query,
        ...(query.page !== undefined && { page: query.page }),
        ...(query.perPage !== undefined && { perPage: query.perPage }),
      },
      secure: true,
      format: 'json',
    });
  }

  public async recordUsageEvent(data: {
    name: string;
    url: string;
    title?: string;
    referrer?: string;
    data?: Record<string, unknown>;
  }): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/usage/record`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }
}
